import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useStore } from 'common/store';
import type { UseStoreValues } from 'common/types/store';
import {
  CellHoverContext,
  useChipPresent,
  useEnteredCurrency,
} from 'components/CellAnnotationValueViewer/components/MoneyChipValueViewer';
import { usePatchCapTableSecurities } from 'services/hooks/captable';
import { useReviewDrawerOpen, useReviewerMode, useReviewItemStatus, useReviewStore } from 'store/useReviewStore';
import CitationValueViewerProps from './CitationValueViewer.props';
import {
  citationBadgeSx,
  StyledCitationNumber,
  StyledCitationNumberWrapper,
  StyledCitationValueViewer,
  StyledCitationValueViewerWrapper,
} from './CitationValueViewer.styles';
import { hasInlineControl } from './inlineControlLayout';
import CitationPopover from '../../../../../components/Popovers/citation-popover/CitationPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { EventType, useEventBus } from '../../../../../context';
import { useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import CapatableService from '../../../../../services/captable';
import type { CitationReferenceData } from '../../../../../services/reference-generator/types';
import { removePendingCitationDraft } from '../../utilities/referenceWrites';
import {
  citationReviewStatus,
  hasLiveAiCitation,
  isAiCreatedCitation,
  isSuggestionMetadata,
  reviewStatusOf,
  statusToHighlightVariant,
} from '../../utilities/reviewAggregator';
import { removeCitationFromSecurities } from '../../utilities/securityAiPatches';
import { settleCitationReview } from '../../utilities/settleCitationReview';
import { reviewPendingTextSx } from '../ai-security-finding-popover/AIMergeValueViewer.styles';
import { useCitationContext } from '../captable-table/CitationContext';

const CitationNestingContext = React.createContext(false);

// Footnote-badge gutter geometry, all in the overlay wrapper's LOCAL (unscaled)
// px space. The badge default-anchors 2px from the cell's left edge; on a
// chip-bearing cell it moves just past the chip's measured right edge plus a
// small gap (matching the chip row's own 4px item gap). When the chip exists but
// cannot be measured yet (first paint, jsdom) a fixed reserve stands in:
// wrapper padding (4) + widest expected 3-letter chip (~28) + gap.
const BADGE_LEFT_DEFAULT_PX = 2;
const CHIP_BADGE_GAP_PX = 4;
const CHIP_RESERVED_OFFSET_PX = 36;

const CitationValueViewerRoot = ({
  value,
  citationReferences,
  citationNumber,
  originalValueViewer: OriginalValueViewer,
  valueReviewApplicable = true,
  citation,
  ...restProps
}: CitationValueViewerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const interactionActiveRef = useRef(false);
  const { setActiveCitation, activeCitation } = useCitationContext();
  const { publish } = useEventBus();
  const { getSourceNumber } = useDocumentFootnotes();
  const patchSecurities = usePatchCapTableSecurities();
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;
  useReviewStore(s => s.reviewItems);
  const capTableService = useMemo(() => new CapatableService(), []);
  const { cell } = restProps;
  const latestReviewState = useRef({ captableInfo, cell });
  latestReviewState.current = { captableInfo, cell };

  // LIVE cell refs, never the `citationReferences` prop: the review settle
  // paths (handleReviewSettleCitation, accept/dismissFieldOnGrid) reassign
  // cell.citationReferences IN PLACE and this viewer re-renders through its
  // review-store subscription (or a hover), where the prop still holds the
  // pre-settle array captured by the last full datasheet render - keying on it
  // froze a citation-only accept's badge at status=pending until reload
  // (SCAL-9336 re-fix).
  const liveCitationReferences = restProps.cell.citationReferences;

  // When the cell also renders a Pre-FX currency chip (cap-table OIP), the chip
  // is the leftmost element; shift the citation footnote to its right so the
  // two don't overlap (SCAL-9062). The chip reveals on hover OR selection, so
  // pass the same hovered-or-selected signal here to engage the offset exactly
  // when it shows. The hook is called unconditionally at the top level (React
  // hooks rules).
  const chipPresent = useChipPresent(restProps.cell, isHovered || Boolean(restProps.selected));

  // The chip's true width depends on font metrics, the ISO code and the sheet
  // zoom, so the gutter is MEASURED, not guessed: this viewer wraps the chip's
  // subtree, so the rendered chip (marked `data-money-chip` on its shell) is
  // findable under the overlay wrapper. Rect deltas come back in viewport space
  // (scaled by any ancestor zoom transform); dividing by rectWidth/offsetWidth
  // converts them to the local px space `left` positions in. Re-measures on
  // every trigger that can change the chip's footprint - mount/unmount
  // (chipPresent), the entered ISO code, and a datasheet re-parse (cell
  // identity, which zoom and currency changes flow through); the guarded
  // setState settles in one extra pass and never loops.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const enteredCurrency = useEnteredCurrency(restProps.cell.cellAnnotationCellKey);
  const [measuredChipOffset, setMeasuredChipOffset] = useState<number | null>(null);
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const chip = chipPresent && wrapper ? wrapper.querySelector('[data-money-chip]') : null;
    let next: number | null = null;
    if (chip && wrapper) {
      const wrapperRect = wrapper.getBoundingClientRect();
      const chipRect = chip.getBoundingClientRect();
      if (chipRect.width > 0 && wrapperRect.width > 0) {
        const scale = wrapper.offsetWidth > 0 ? wrapperRect.width / wrapper.offsetWidth : 1;
        next = Math.round((chipRect.right - wrapperRect.left) / scale) + CHIP_BADGE_GAP_PX;
      }
    }
    setMeasuredChipOffset(prev => (prev === next ? prev : next));
    // The extra deps are re-measure TRIGGERS (they change the chip's rendered
    // width), not values the measurement reads from React state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chipPresent, enteredCurrency, restProps.cell]);
  const citationOffsetLeft = chipPresent ? (measuredChipOffset ?? CHIP_RESERVED_OFFSET_PX) : BADGE_LEFT_DEFAULT_PX;

  // Grid-visible citations (SCAL-8969): a DISMISSED citation never renders a
  // footnote badge - it stays reachable (grayed) from popover/review surfaces only.
  // The cell owns one aggregate badge/popover. Keep every live citation row in
  // that popover (including multiple excerpts from one document), filtering only
  // dismissed rows per the existing grid rule.
  const visibleCitations = useMemo(
    () => (liveCitationReferences ?? []).filter(cit => citationReviewStatus(cit) !== 'dismissed'),
    [liveCitationReferences]
  );

  const citationSourceNumbers = useMemo(
    () =>
      [
        ...new Set(
          visibleCitations
            .map(cit => getSourceNumber(cit.document_reference))
            .filter((sourceNumber): sourceNumber is number => sourceNumber !== undefined)
        ),
      ].sort((a, b) => a - b),
    [visibleCitations, getSourceNumber]
  );
  const citationSourceLabel = citationSourceNumbers.join(',');

  const cancelClose = React.useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = React.useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      if (!interactionActiveRef.current) {
        setAnchorEl(null);
      }
    }, 120);
  }, [cancelClose]);

  const handleInteractionActiveChange = React.useCallback(
    (active: boolean) => {
      interactionActiveRef.current = active;
      if (active) {
        cancelClose();
      } else {
        scheduleClose();
      }
    },
    [cancelClose, scheduleClose]
  );

  const titleCell = useMemo(() => {
    const columnId = restProps.cell.columnLegend;
    const titleCellForColumn = restProps.cell.sheet?.cells?.[`${columnId}1`];
    return titleCellForColumn?.value ? (titleCellForColumn.value as string) : 'Unknown...';
  }, [restProps.cell]);

  const handleBadgeClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleCitationDelete = (citationId: number) => {
    if (activeCitation?.id === citationId) {
      setActiveCitation(undefined);
    }

    patchSecurities(securities => removeCitationFromSecurities(securities, citationId) as typeof securities);
  };

  const handleRemovePendingCitation = (draft: CitationReferenceData) => {
    if (activeCitation === draft) {
      setActiveCitation(undefined);
    }
    patchSecurities(securities => removePendingCitationDraft(securities, cell.columnRef, draft));
    setAnchorEl(null);
  };

  const handleCitationSettle = useCallback(
    async (citation: CitationReferenceData, action: 'accept' | 'dismiss') => {
      const citationId = citation.id;
      if (citationId == null) {
        return;
      }
      try {
        await settleCitationReview({
          citationId,
          action,
          persist: capTableService.reviewCitation,
          getCell: () => latestReviewState.current.cell,
          getSecurities: () => latestReviewState.current.captableInfo?.securities,
          getReviewStore: useReviewStore.getState,
        });
      } catch (error) {
        enqueueSnackbar(`Failed to ${action} the citation`, { variant: 'error' });
        throw error;
      }
    },
    [capTableService]
  );

  const citationContainsActive = useMemo(
    () => visibleCitations.some(cit => cit.id === activeCitation?.id),
    [visibleCitations, activeCitation]
  );

  // Dropdown and date-picker cells own the cell interior, so the badge cannot
  // overlay them. Reserve a stable left gutter sized to the badges (so the
  // indicator stays usable and the layout never jumps) instead (SCAL-8987).
  const reservedBadgeWidth = useMemo(() => {
    if (!hasInlineControl(restProps.cell)) {
      return 0;
    }
    // One flexible-width badge plus the 2px left offset and a small margin.
    return Math.max(24, citationSourceLabel.length * 6 + 8) + 6;
  }, [restProps.cell, citationSourceLabel.length]);

  // Purple text indicates a pending VALUE suggestion. Citation-only review
  // changes the badge, while the saved value keeps its existing appearance.
  const drawerOpen = useReviewDrawerOpen();
  // SCAL-9335: only analyst-side users see review styling at all. For everyone
  // else the badges below render normal blue regardless of citation status, and
  // the pending-purple rest state never engages.
  const reviewerMode = useReviewerMode();
  const { columnId, alias } = restProps.cell;
  const storeStatus = useReviewItemStatus(columnId != null ? Number(columnId) : undefined, alias);
  const fieldMetadata = restProps.cell.aiFieldMetadata;
  const valueStatus = storeStatus ?? reviewStatusOf(isSuggestionMetadata(fieldMetadata) ? fieldMetadata : null);
  const restIsPurple = valueReviewApplicable && reviewerMode && drawerOpen && valueStatus === 'pending';
  // A pending badge means a pending AI citation: citationReviewStatus folds
  // provenance in, so a human citation never counts whatever its flags say.
  const hasPendingCitation = reviewerMode && visibleCitations.some(cit => citationReviewStatus(cit) === 'pending');
  const badgeStatus = hasPendingCitation ? 'pending' : 'accepted';
  const badgeAiCreated = reviewerMode && hasLiveAiCitation(visibleCitations);
  const citationEntries = visibleCitations.map(cit => ({
    citation: cit,
    aiCreated: isAiCreatedCitation(cit),
  }));
  const citationCountLabel = `${visibleCitations.length} ${
    visibleCitations.length === 1 ? 'citation' : 'citations'
  } from ${citationSourceNumbers.length === 1 ? 'source' : 'sources'} ${citationSourceLabel}`;

  // The badge reveals on hover, while its popover is open, or while one of its
  // citations is the active PDF trace. With the review drawer open a PENDING
  // AI citation also shows at rest, so the panel's open items are findable on
  // the grid; accepted and human citations stay hover-only.
  const showCitationBadge =
    isHovered || Boolean(anchorEl) || citationContainsActive || (drawerOpen && hasPendingCitation);

  // Citation visibility follows its own review state, independent of the value.
  // Rejected citations were filtered out above; accepted ones keep their badge.
  if (visibleCitations.length === 0) {
    if (OriginalValueViewer) {
      return <OriginalValueViewer value={value} {...restProps} />;
    }
    return <FastValueViewer value={value} {...restProps} />;
  }

  return (
    // The overlay is the cell's ONE hover region: its isHovered drives the
    // badge reveal AND (via CellHoverContext) the Pre-FX chip deeper in the
    // subtree, so the chip survives the pointer crossing onto the badge - a
    // sibling overlay the chip's own flex row would treat as a mouseleave.
    <StyledCitationValueViewerWrapper
      ref={wrapperRef}
      data-testid="citation-cell-overlay"
      onMouseEnter={() => {
        cancelClose();
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        scheduleClose();
        setIsHovered(false);
      }}>
      {showCitationBadge && (
        <StyledCitationNumberWrapper style={{ left: citationOffsetLeft }}>
          <StyledCitationNumber
            data-testid="citation-badge"
            data-review-status={badgeStatus}
            data-ai-created={badgeAiCreated || undefined}
            aria-label={citationCountLabel}
            onMouseDown={e => e.stopPropagation()}
            onClick={handleBadgeClick}
            sx={citationBadgeSx({
              pending: badgeStatus === 'pending',
              aiCreated: badgeAiCreated,
              active: Boolean(anchorEl) || citationContainsActive,
            })}>
            {citationSourceLabel}
          </StyledCitationNumber>
        </StyledCitationNumberWrapper>
      )}

      <StyledCitationValueViewer
        reservedBadgeWidth={reservedBadgeWidth}
        data-review-purple={restIsPurple || undefined}
        sx={restIsPurple ? reviewPendingTextSx : undefined}>
        <CellHoverContext.Provider value={isHovered}>
          {OriginalValueViewer ? (
            <OriginalValueViewer value={value} {...restProps} />
          ) : (
            <FastValueViewer className="text-cell" value={value} {...restProps} />
          )}
        </CellHoverContext.Provider>
      </StyledCitationValueViewer>

      {anchorEl && (
        <CitationPopover
          onView={cit => {
            // Highlight color follows the CITATION'S review status (SCAL-8986), not
            // the navigation source: a still-pending citation traced from the cell
            // footnote reads AI-purple, an accepted/manual one plain yellow. For
            // non-reviewers (SCAL-9335) the trace is always the plain variant.
            setActiveCitation(cit, statusToHighlightVariant(reviewerMode ? citationReviewStatus(cit) : 'accepted'));
            publish(EventType.NAVIGATE_TO_DOCUMENT_REFERENCE, {
              documentReferenceId: cit.document_reference,
              expandDrawer: true,
              activeCitationData: cit,
            });
          }}
          open
          anchorEl={anchorEl}
          onClose={scheduleClose}
          entries={citationEntries}
          columnarIdentifier={titleCell}
          // Staff see AI vs human. Everyone else gets one unlabeled list.
          showProvenance={reviewerMode}
          onDelete={handleCitationDelete}
          onRemovePending={handleRemovePendingCitation}
          onAccept={citationToSettle => handleCitationSettle(citationToSettle, 'accept')}
          onReject={citationToSettle => handleCitationSettle(citationToSettle, 'dismiss')}
          onInteractionActiveChange={handleInteractionActiveChange}
          slotProps={{
            paper: {
              onMouseEnter: cancelClose,
              onMouseLeave: scheduleClose,
            },
          }}
        />
      )}
    </StyledCitationValueViewerWrapper>
  );
};

const CitationValueViewer = ({ valueReviewApplicable, ...props }: CitationValueViewerProps) => {
  const isNested = React.useContext(CitationNestingContext);

  if (isNested) {
    const {
      value,
      originalValueViewer: OriginalValueViewer,
      citationReferences: _citationReferences,
      citationNumber: _citationNumber,
      citation: _citation,
      ...restProps
    } = props;
    return OriginalValueViewer ? (
      <OriginalValueViewer value={value} {...restProps} />
    ) : (
      <FastValueViewer value={value} {...restProps} />
    );
  }

  return (
    <CitationNestingContext.Provider value>
      <CitationValueViewerRoot {...props} valueReviewApplicable={valueReviewApplicable} />
    </CitationNestingContext.Provider>
  );
};

export default CitationValueViewer;
